import axios from "axios";
import i18n from "i18next";
import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Container, Form, InputGroup, Row } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import type { I18n } from "../../types/I18n";
import type { ValueLabel } from "../../types/ValueLabel";
import type { RecommendedEarningsResponse } from "../../types/yttari/RecommendedEarnings";
import { trackEvent } from "../../utils/piwik-util";
import LargeSelectInput from "../LargeSelectInput";
import CodesContext from "../CodesContext";
import ResultsGroup from "../ResultsGroup";
import { useCodeList } from "../useCodeList";
import { useIndustries } from "../useIndustries";
import { useValidation } from "../useValidation";
import { useValidationOnLanguageChange } from "../useValidationOnLanguageChange";
import ValidationContext from "../ValidationContext";
import ValidationFeedback from "../ValidationFeedback";

const YttariCalculator = () => {
  const industries = useCodeList<ValueLabel<string, I18n>>("industry");
  return (
    <CodesContext.Provider value={{ industries }}>
      <YttariView />
    </CodesContext.Provider>
  );
};

const YttariView: React.FC = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { options } = useIndustries();

  const [selectedIndustry, setSelectedIndustry] = useState<
    ValueLabel<string> | null | undefined
  >(null);
  const [estimatedRevenue, setEstimatedRevenue] = useState<string>("");
  const [recommendedEarnings, setRecommendedEarnings] =
    useState<RecommendedEarningsResponse | null>(null);
  const [responseError, setResponseError] = useState<string | null>(null);
  const resultsRegionRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (selectedIndustry) {
      setSelectedIndustry(
        options.find((o) => o.value === selectedIndustry.value) || null,
      );
    }
  }, [language]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (recommendedEarnings || responseError) {
      resultsRegionRef.current?.focus();
      resultsRegionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [recommendedEarnings, responseError]);

  const onSubmit = (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setRecommendedEarnings(null);
    setResponseError(null);

    axios
      .post<RecommendedEarningsResponse>(
        `/api/calculate-earnings?language=${language}`,
        {
          industry: selectedIndustry?.value,
          estimatedRevenue: Number.parseInt(
            estimatedRevenue.trim().replaceAll(/\s/g, ""),
          ),
        },
      )
      .then((response) => {
        if (response.data) {
          setRecommendedEarnings(response.data);
        }
      })
      .catch((error) => {
        setResponseError(error.response?.data?.message || t("errorUnknown"));
      });
  };

  return (
    <>
      <Container className="calculator-container content-container">
        <h1>{t("yttari.calculatorTitle")}</h1>
        <p className="ingress">{t("yttari.ingress")}</p>
      </Container>

      <div className="form-container py-4">
        <Container className="content-container">
          <Row>
            <Col>
              <EarningsForm
                selectedIndustry={selectedIndustry}
                setSelectedIndustry={setSelectedIndustry}
                estimatedRevenue={estimatedRevenue}
                setEstimatedRevenue={setEstimatedRevenue}
                onSubmit={onSubmit}
              />
            </Col>
          </Row>
          <Row>
            <Col
              id="results-region"
              role="region"
              md={12}
              className="mt-4"
              ref={resultsRegionRef}
              tabIndex={-1}
            >
              {responseError && (
                <div>
                  <h2>{responseError}</h2>
                </div>
              )}
              <RecommendedEarnings recommendedEarnings={recommendedEarnings} />
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

interface EarningsFormProps {
  selectedIndustry: ValueLabel<string> | null | undefined;
  setSelectedIndustry: (
    industry: ValueLabel<string> | null | undefined,
  ) => void;
  estimatedRevenue: string;
  setEstimatedRevenue: (estimatedRevenue: string) => void;
  onSubmit: (event: React.SyntheticEvent<HTMLFormElement>) => void;
}

const EarningsForm = ({
  selectedIndustry,
  setSelectedIndustry,
  estimatedRevenue,
  setEstimatedRevenue,
  onSubmit,
}: EarningsFormProps): React.ReactNode => {
  const { t } = useTranslation();
  const { options } = useIndustries();
  const validation = useValidation();
  const formRef = useRef<HTMLFormElement>(null);
  const errorSummary = useRef<HTMLDivElement>(null);
  useValidationOnLanguageChange(
    formRef,
    validation.validateForm,
    validation.validated,
  );

  const [revenueChanged, setRevenueChanged] = useState<boolean>(false);
  const [revenueValid, setRevenueValid] = useState<boolean>(true);
  const revenueUnitId = "yttari-revenue-unit";
  const revenueErrorId = "yttari-revenue-error";
  const isRevenueInvalid = validation.validated && !revenueValid;
  const suppressFieldErrors = validation.validationFeedback.length > 0;

  const handleSubmit = (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (validation.validate(event)) {
      onSubmit(event);
    } else if (errorSummary.current) {
      errorSummary.current.focus();
      errorSummary.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const onRevenueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setRevenueChanged(true);
    setRevenueValid(!!value && /[0-9 ]/.test(value));
    setEstimatedRevenue(value.trim().replaceAll(/\s/g, ""));
  };

  return (
    <ValidationContext.Provider value={validation}>
      <Form
        ref={formRef}
        noValidate
        validated={validation.validated}
        onSubmit={handleSubmit}
        className="calculator-form"
      >
        <ValidationFeedback
          feedback={validation.validationFeedback}
          ref={errorSummary}
        />
        <p className="required-fields-info">
          <span className="required">*</span>&nbsp;
          {t("requiredFieldsInfo")}
        </p>
        <Row>
          <Col md={12}>
            <Form.Group className="form-group industry-select">
              <LargeSelectInput
                fieldName="yttari.selectIndustry"
                required={true}
                showValidation={validation.validated}
                options={options}
                value={selectedIndustry}
                setValue={setSelectedIndustry}
                searchable={true}
                clearable={true}
                placeholder={t("yttari.selectIndustry.placeholder")}
                noResultsText={t("yttari.selectIndustry.noOptions")}
                label={
                  <>
                    <h2 className="error-label">
                      {t("yttari.selectIndustry.title")}
                      <span className="required">*</span>
                    </h2>
                    <p>
                      <Trans
                        i18nKey="yttari.selectIndustry.explanation"
                        components={{
                          Link: (
                            <a href={t("yttari.selectIndustry.tolUrl")}>
                              stat.fi
                            </a>
                          ),
                        }}
                      />
                    </p>
                  </>
                }
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Form.Group className={"form-group value-input"}>
            <Col md={12}>
              <Form.Label htmlFor={"estimated-revenue"}>
                <h2 className="error-label">
                  {t("yttari.revenue.title")}
                  <span className="required">*</span>
                </h2>
                <p>{t("yttari.revenue.explanation")}</p>
              </Form.Label>
            </Col>
            <Col sm={12}>
              <InputGroup
                hasValidation
                className={`${
                  validation.validated ? "was-validated" : "was-not-validated"
                }`}
              >
                <Form.Control
                  id="estimated-revenue"
                  className={`value-input ${revenueChanged ? "changed" : ""} ${
                    revenueValid ? "" : "invalid is-invalid"
                  }`}
                  value={estimatedRevenue}
                  onChange={onRevenueChange}
                  pattern="[1-9][0-9]{0,9}"
                  maxLength={10}
                  placeholder=""
                  required
                  aria-describedby={`${revenueUnitId} ${
                    isRevenueInvalid && !suppressFieldErrors
                      ? revenueErrorId
                      : ""
                  }`
                    .trim()
                    .replace(/\s+/g, " ")}
                  aria-invalid={isRevenueInvalid || undefined}
                />
                <InputGroup.Text id={revenueUnitId}>
                  {t("yttari.eurYear")}
                </InputGroup.Text>
                <Form.Control.Feedback
                  id={revenueErrorId}
                  type="invalid"
                  aria-hidden={suppressFieldErrors || undefined}
                >
                  {t("yttari.revenue.invalid")}
                </Form.Control.Feedback>
              </InputGroup>
            </Col>
          </Form.Group>
        </Row>
        <Row>
          <Col sm={6} className="mt-3">
            <Button
              type="submit"
              aria-controls="results-region"
              onClick={() => {
                trackEvent("Button", t("yttari.calculate"));
              }}
            >
              {t("yttari.calculate")}
            </Button>
          </Col>
        </Row>
      </Form>
    </ValidationContext.Provider>
  );
};

interface RecommendedEarningsProps {
  recommendedEarnings: RecommendedEarningsResponse | null;
}

const RecommendedEarnings = ({
  recommendedEarnings,
}: RecommendedEarningsProps): React.ReactNode => {
  const { t } = useTranslation();

  if (!recommendedEarnings) {
    return null;
  }

  const formatNumber = (number: number, precision: number = 0) => {
    return new Intl.NumberFormat("fi-FI", {
      maximumFractionDigits: precision,
    }).format(number);
  };

  const forOccupation = recommendedEarnings.medianEarningsOccupation;

  return (
    <ResultsGroup
      legend={t("yttari.result.title")}
      className="results-container"
    >
      <div className="result-value mb-3">
        {t("yttari.result.recommended", {
          recommended: formatNumber(recommendedEarnings.recommendedEarnings),
        })}
      </div>
      <p>
        <Trans
          i18nKey="yttari.result.earnings.leeway"
          components={{ strong: <strong>bounds</strong> }}
          values={{
            lower: formatNumber(recommendedEarnings.earningsLowerLimit),
            upper: formatNumber(recommendedEarnings.earningsUpperLimit),
          }}
        />
        {recommendedEarnings.infos.includes("AUTYOTUL_009") ? (
          <> {t("yttari.result.earnings.lowerLimitUnder")}</>
        ) : null}
        {recommendedEarnings.infos.includes("AUTYOTUL_008") ? (
          <> {t("yttari.result.earnings.upperLimitOver")}</>
        ) : null}{" "}
        {t("yttari.result.earnings.exception")}
      </p>
      <div className={"calculationInfo mt-5"}>
        <h3>{t("yttari.result.calculationInfo.title")}</h3>
        <ul>
          <li>
            <Trans
              i18nKey={`yttari.result.calculationInfo.medianRevenue.${
                recommendedEarnings.medianRevenueIndustry?.value === "KAIK"
                  ? "allIndustries"
                  : "forIndustry"
              }`}
              values={{
                medianRevenue: formatNumber(
                  recommendedEarnings.industryMedianRevenue,
                ),
                industryCode: recommendedEarnings.medianRevenueIndustry?.value,
                industryName:
                  recommendedEarnings.medianRevenueIndustry?.label[
                    i18n.language
                  ],
              }}
            />
          </li>
          <li>
            <Trans
              i18nKey={`yttari.result.calculationInfo.medianEarnings.${
                recommendedEarnings.medianEarningsIndustry?.value === "KAIK"
                  ? "allIndustries"
                  : forOccupation
                    ? "forOccupation"
                    : "forIndustry"
              }`}
              values={{
                medianEarnings: formatNumber(
                  recommendedEarnings.industryMedianEarnings,
                ),
                occupationCode: forOccupation?.value,
                occupationName: forOccupation?.label[i18n.language],
                industryCode: recommendedEarnings.medianEarningsIndustry?.value,
                industryName:
                  recommendedEarnings.medianEarningsIndustry?.label[
                    i18n.language
                  ],
              }}
            />
          </li>
          <li>{t("yttari.result.calculationInfo.footer")}</li>
        </ul>
      </div>
      <hr className="divider" aria-hidden="true" />
      <p>
        <Trans
          i18nKey="yttari.result.extraInfo.paragraph1"
          components={{
            Link: (
              <a href={t("yttari.result.extraInfo.contactsUrl")}>
                tyoelake.fi/contacts
              </a>
            ),
          }}
        />
      </p>
      <p>
        <Trans
          i18nKey="yttari.result.extraInfo.paragraph2"
          components={{
            Link: <a href={t("yttari.result.extraInfo.legalUrl")}>etk.fi</a>,
          }}
        />
      </p>
      <p>
        <Trans
          i18nKey="yttari.result.extraInfo.paragraph3"
          components={{
            Link: (
              <a href={t("yttari.result.extraInfo.yelInfoUrl")}>
                tyoelake.fi/pensions
              </a>
            ),
          }}
        />
      </p>
    </ResultsGroup>
  );
};

export default YttariCalculator;
